import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Id of the visible title; use `label` when there is none */
  labelledBy?: string;
  label?: string;
  /** Grab handle that drags the sheet down to close it on small screens */
  handle?: boolean;
  /** `muted` gives a grey panel for grouped white lists, as in iOS settings */
  surface?: 'white' | 'muted';
  /** Element to focus when the dialog opens; defaults to the panel */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  children: React.ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Floating panel over a blurred backdrop. A bottom sheet on small screens.
 * Closes on Escape and backdrop click, traps focus, and restores it on close.
 */
export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  labelledBy,
  label,
  handle = false,
  surface = 'white',
  initialFocusRef,
  className = '',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; dy: number; startedAt: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const onHandlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    // The entrance animation holds the transform, so stop it before dragging
    panel.style.animation = 'none';
    panel.style.transition = 'none';
    drag.current = { startY: event.clientY, dy: 0, startedAt: performance.now() };
  };

  const onHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!drag.current || !panel) return;
    drag.current.dy = Math.max(0, event.clientY - drag.current.startY);
    panel.style.transform = `translateY(${drag.current.dy}px)`;
  };

  const onHandlePointerUp = () => {
    const panel = panelRef.current;
    const state = drag.current;
    drag.current = null;
    if (!state || !panel) return;
    const velocity = state.dy / Math.max(1, performance.now() - state.startedAt);
    if (state.dy > 96 || (state.dy > 24 && velocity > 0.6)) {
      panel.style.transition = 'transform 180ms ease-in';
      panel.style.transform = 'translateY(100%)';
      window.setTimeout(() => onCloseRef.current(), 170);
    } else {
      panel.style.transition = 'transform 240ms cubic-bezier(0.16, 1, 0.3, 1)';
      panel.style.transform = '';
    }
  };

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => (initialFocusRef?.current ?? panelRef.current)?.focus(), 30);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Don't scroll the page back to the opener (it may be on a page we just navigated away from)
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  return createPortal(
    // A column, so the panel (overflow-hidden, hence no minimum height) shrinks to the screen and its
    // DialogBody scrolls: a sheet taller than a phone otherwise ran off the top with no way to reach it
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-end pt-[max(0.75rem,env(safe-area-inset-top))] font-ui sm:justify-start sm:px-6 sm:pb-6 sm:pt-[8vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="pointer-events-none fixed inset-0 animate-overlay-in bg-neutral-900/[0.08] backdrop-blur-md"
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
        className={`relative flex w-full animate-sheet-in flex-col overflow-hidden rounded-t-[28px] ${surface === 'muted' ? 'bg-neutral-100' : 'bg-white'} shadow-[0_40px_120px_-24px_rgba(0,0,0,0.28)] ring-1 ring-black/[0.06] focus:outline-none sm:animate-panel-in sm:rounded-[28px] ${className}`}
      >
        {handle && (
          <div
            aria-hidden="true"
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerUp}
            className="flex h-6 flex-shrink-0 touch-none items-center justify-center sm:hidden"
          >
            <span className="h-1 w-9 rounded-full bg-neutral-300" />
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
};

/** Title row with a round close button */
export const DialogHeader: React.FC<{
  id?: string;
  title: React.ReactNode;
  /** Small grey line above the title */
  eyebrow?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}> = ({ id, title, eyebrow, onClose, className = '' }) => (
  <div className={`flex items-start justify-between gap-4 px-5 pt-4 sm:pt-5 ${className}`}>
    <div className="min-w-0">
      {eyebrow && <p className="text-[12px] text-neutral-400">{eyebrow}</p>}
      <h2 id={id} className="text-[18px] font-normal tracking-[-0.02em] text-ink">
        {title}
      </h2>
    </div>
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="-mr-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-600 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    )}
  </div>
);

export const DialogBody: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`min-h-0 flex-1 overflow-y-auto px-5 py-4 ${className}`}>{children}</div>
);

/** Actions row; on phones it respects the home indicator area */
export const DialogFooter: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div
    className={`flex flex-shrink-0 items-center justify-end gap-2 border-t border-neutral-200/70 px-5 py-3 ${className}`}
    style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
  >
    {children}
  </div>
);
